// components
import { LabelColor } from 'src/components/label';

// ----------------------------------------------------------------------
// Shared helpers for the ransomware intelligence module.

export const IOC_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'md5', label: 'MD5' },
  { value: 'sha256', label: 'SHA256' },
  { value: 'ip', label: 'IP' },
  { value: 'domain', label: 'Domain' },
  { value: 'email', label: 'Email' },
  { value: 'btc', label: 'BTC' },
  { value: 'url', label: 'URL' },
];

// Raw API ioc-type key -> display label (e.g. "md5" -> "MD5").
export function iocTypeLabel(value: string): string {
  return IOC_TYPE_OPTIONS.find((opt) => opt.value === value)?.label ?? value.toUpperCase();
}

// Years offered in the victim filter (current year back to 2020).
export function yearOptions(): string[] {
  const current = new Date().getFullYear();
  const years: string[] = [];
  for (let y = current; y >= 2020; y -= 1) years.push(String(y));
  return years;
}

// CVSS severity -> Label color
export function getCvssColor(severity: string | null | undefined): LabelColor {
  switch ((severity || '').toUpperCase()) {
    case 'CRITICAL':
      return 'error';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'info';
    case 'LOW':
      return 'success';
    default:
      return 'default';
  }
}

// A group is "active" if last seen within ~90 days.
export function isGroupActive(lastseen: string | null | undefined): boolean {
  if (!lastseen) return false;
  const ts = Date.parse(lastseen);
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= 90 * 86_400_000;
}

// ISO alpha-2 -> flag emoji (regional indicator symbols).
export function countryFlag(code: string | null | undefined): string {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return '';
  return String.fromCodePoint(
    0x1f1e6 + upper.charCodeAt(0) - 65,
    0x1f1e6 + upper.charCodeAt(1) - 65
  );
}

const REGION_NAMES =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

// ISO alpha-2 -> country name, falling back to the code.
export function countryName(code: string | null | undefined): string {
  if (!code) return 'Unknown';
  try {
    return REGION_NAMES?.of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
}

// Sort a Record<key, count> into a descending [key, count] list.
export function sortedEntries(
  record: Record<string, number> | undefined
): [string, number][] {
  if (!record) return [];
  return Object.entries(record).sort((a, b) => b[1] - a[1]);
}

// ----------------------------------------------------------------------
// Terminology & display standardization (single source of truth).
// The backend stores actors as lowercase canonical slugs (e.g. "lockbit3");
// the UI must present them consistently. These helpers replace the ad-hoc
// `textTransform:'capitalize'` / raw-token rendering the audit flagged.

// Canonical actor slug -> display name. Title-cases each word but preserves
// embedded digits/version tokens (lockbit3 -> "Lockbit3", alphv -> "Alphv").
export function actorLabel(actor: string | null | undefined): string {
  if (!actor) return 'Unknown actor';
  return actor
    .split(/[\s_-]+/)
    .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// Sector/industry display — filters the upstream "Not Found" sentinel.
export function sectorLabel(sector: string | null | undefined): string {
  if (!sector || sector === 'Not Found') return 'Unspecified';
  return sector;
}

// ISO alpha-2 -> "🇺🇸 United States" (flag + full name), never the bare code.
export function countryLabel(code: string | null | undefined): string {
  if (!code) return 'Unattributed';
  const name = countryName(code);
  const flag = countryFlag(code);
  return flag ? `${flag} ${name}` : name;
}

// Insight severity enum -> proper label.
export function severityLabel(severity: string | null | undefined): string {
  switch ((severity || '').toLowerCase()) {
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'info':
      return 'Informational';
    default:
      return severity || '';
  }
}

export function severityColor(severity: string | null | undefined): LabelColor {
  switch ((severity || '').toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'default';
  }
}

// Insight category slug -> readable label.
const INSIGHT_CATEGORY_LABELS: Record<string, string> = {
  'actor-trend': 'Actor momentum',
  'sector-trend': 'Sector trend',
  'country-trend': 'Country trend',
  'new-actor': 'Emerging actor',
  'actor-expansion': 'Expansion',
  'rank-change': 'Ranking shift',
  spike: 'Activity spike',
  'repeat-target': 'Re-victimization',
  'top-actor': 'Top actor',
  campaign: 'Campaign',
  'peak-period': 'Peak period',
  cve: 'Vulnerability',
  tooling: 'Shared tooling',
  press: 'Press coverage',
};

export function insightCategoryLabel(category: string): string {
  return INSIGHT_CATEGORY_LABELS[category] ?? category.replace(/-/g, ' ');
}

// Confidence band -> {label, color} for the ConfidenceChip.
export type ConfidenceBand = 'low' | 'medium' | 'high';

export function confidenceLabel(band: ConfidenceBand | string): string {
  switch (band) {
    case 'high':
      return 'Corroborated';
    case 'medium':
      return 'Partial';
    case 'low':
    default:
      return 'Unverified';
  }
}

export function confidenceColor(band: ConfidenceBand | string): LabelColor {
  switch (band) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
    default:
      return 'default';
  }
}
